<script setup lang="ts">
  import { computed } from 'vue';

  import type { Attachment } from '@stuut/api/models';
  import { Dialog, DialogContent, DialogTrigger } from '@ui/components/dialog';
  import { cn } from '@ui/utils/utils';

  import AttachmentPreview from '@/components/attachments/AttachmentPreview.vue';

  interface Props {
    attachment?: Attachment;
    dashed?: boolean;
    failed?: boolean;
    finalized?: boolean;
    hideDelete?: boolean;
    leftIcon?: string;
    leftIconClasses?: string;
    leftIconHover?: boolean;
    maxLength?: number;
    rightIcon?: string;
    rightIconClasses?: string;
    rightIconHover?: boolean;
    showLeftIcon?: boolean;
    showRightIcon?: boolean;
    truncate?: boolean;
    uploading?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    hideDelete: true,
    leftIcon: 'fa-paperclip',
    leftIconHover: false,
    maxLength: 24,
    rightIconHover: false,
    showLeftIcon: true,
    showRightIcon: false,
  });

  const emits = defineEmits<{
    (e: 'leftIconClick'): void;
    (e: 'rightIconClick'): void;
    (e: 'delete'): void;
  }>();

  const isOpen = defineModel<boolean>('open', { default: false });

  // Surface a delete failure (set by the API layer when an optimistic remove is rolled
  // back) as well as any explicit `failed` prop from the caller.
  const isFailed = computed(() => props.failed || props.attachment?.failed_to_delete);

  const displayText = computed(() => {
    if (props.attachment?.name) {
      return props.truncate && props.attachment.name.length > props.maxLength
        ? `${props.attachment.name.slice(0, props.maxLength)}...`
        : props.attachment.name;
    }

    return '';
  });
</script>

<template>
  <div>
    <Dialog v-model:open="isOpen">
      <DialogTrigger
        class="flex flex-row w-full text-xs max-w-fit px-2 py-1 border gap-x-2 items-center rounded bg-muted/30 group"
        :class="{
          'border-dashed': dashed,
          'pointer-events-none': uploading,
          'border-destructive': isFailed,
        }"
        as-child
      >
        <span class="font-medium text-2xs space-x-1.5">
          <i
            v-if="showLeftIcon && !uploading"
            :class="
              cn(leftIcon, leftIconClasses, {
                'text-agent': !leftIconClasses && !leftIconHover,
                'text-agent/50 hover:text-agent cursor-pointer': !leftIconClasses && leftIconHover,
                'fa fa-solid': !leftIconClasses,
              })
            "
            @click="
              (e) => {
                e.stopPropagation();
                emits('leftIconClick');
              }
            "
          />

          <span
            class="cursor-pointer"
            :class="
              isFailed
                ? 'text-destructive'
                : attachment?.src
                  ? 'text-blue hover:text-blue/50'
                  : 'text-foreground'
            "
            :title="attachment?.name"
          >
            <slot v-if="$slots.preview_trigger" name="preview_trigger"></slot>
            <template v-else>
              {{ displayText }}
            </template>
          </span>

          <i
            v-if="showRightIcon && !uploading"
            :class="
              cn(rightIcon, rightIconClasses, {
                'text-agent': !rightIconClasses && !rightIconHover,
                'text-agent/50 hover:text-agent cursor-pointer':
                  !rightIconClasses && rightIconHover,
                'fa fa-solid': !rightIconClasses,
              })
            "
            @click="
              (e) => {
                e.stopPropagation();
                emits('rightIconClick');
              }
            "
          ></i>

          <i v-if="uploading" class="fa fa-spinner-third animate-spin" />
          <i
            v-else-if="!finalized && !hideDelete"
            class="fa fa-times cursor-pointer text-foreground hover:text-foreground/50"
            @click="
              (e) => {
                e.stopPropagation();
                emits('delete');
              }
            "
          />
        </span>
      </DialogTrigger>

      <DialogContent class="overflow-scroll max-h-[90vh] min-h-[200px] max-w-4xl w-full p-3">
        <slot v-if="$slots.preview_content" name="preview_content"></slot>

        <AttachmentPreview v-else-if="attachment" :attachment="attachment" />
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped></style>
